/**
 * Guards for validating API response payloads before they're consumed by the
 * UI. A request can succeed (200) yet return a malformed/empty body, which
 * would otherwise render a misleading empty state. These helpers let callers
 * detect that and surface the shared refresh affordance instead.
 */

/**
 * Returns true when `data` is non-null and satisfies `predicate`. The type
 * guard narrows `data` to the predicate's narrowed type `S`, so callers can
 * use the result in an `if` and get the narrowed type.
 */
export const isApiDataValid = <S>(
  data: unknown,
  predicate: (data: unknown) => data is S
): data is S => data != null && predicate(data);

/** True when `value` is a non-null object (arrays excluded). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** True when `value` is an array (empty arrays included). */
export const isArray = (value: unknown): value is unknown[] =>
  Array.isArray(value);
